// App.js
import React, { Suspense, lazy, useEffect, useMemo } from 'react';

// Google Maps
import { loadGoogleMaps } from 'googleMapsLoader';

// Stripe
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// Routing
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import usePageViewTracking from 'hooks/usePageViewTracking';
import Home from 'home/Home';
import Main from 'main/Main';
import Root from 'root/Root';

// Context
import { useTheme } from 'context/ThemeContext';
import { ThemeProvider } from 'context/ThemeContext';
import { applyThemeCssVariables, buildThemeCssVariables, clearThemeCssVariables } from 'context/themeVariables';
import AppProviders from 'context/AppProviders';
import { BaseInviteAcceptProvider } from 'context/BaseInviteAcceptContext';
import { useUser } from 'context/UserContext';

// Toast
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// CSS
import 'App.css';

// Components
const Admin = lazy(() => import('admin/Admin'));
const DatabasePage = lazy(() => import('pages/DatabasePage'));
const CollectionPage = lazy(() => import('pages/CollectionPage'));
const Schedule = lazy(() => import('schedule/Schedule'));
const Channels = lazy(() => import('channels/Channel'));
const DashboardPage = lazy(() => import('pages/DashboardPage'));
const Notifications = lazy(() => import('notifications/Notifications'));
const Favorites = lazy(() => import('favorites/Favorites'));
const Trash = lazy(() => import('trash/Trash'));
const NodePage = lazy(() => import('pages/NodePage'));
const Search = lazy(() => import('search/Search'));
const WatchPage = lazy(() => import('pages/WatchPage'));
const Api = lazy(() => import('pages/Api'));
const ProfilePage = lazy(() => import('pages/ProfilePage'));
const BaseInvitePage = lazy(() => import('pages/BaseInvitePage'));
const AuthResetPasswordPage = lazy(() => import('pages/AuthResetPasswordPage'));
const PoSignPortalPage = lazy(() => import('pages/PoSignPortalPage'));
const StudioPage = lazy(() => import('pages/StudioPage'));
const ClickUpImportPage = lazy(() => import('import/clickup/ClickUpImportPage'));
const WorkspaceSidebar = lazy(() => import('workspace/sidebar/WorkspaceSidebar'));
const stripePublishableKey = 'pk_live_51Tf58lH21MbpUXD4qeNMD12A6Oscb9TNypVXSgUQymKmyo74WfFbog5RcFWH6p0nR4F9lppluV6AOMyljiP6EnpS00VTYoOdhA';
const stripePromise = loadStripe(stripePublishableKey).catch((error) => {
  console.warn('Stripe.js failed to load; billing checkout will be unavailable.', error);
  return null;
});

const SuperUserAdminRoute = () => {
  const {
    user,
    isAuthLoading,
    isAuthClaimsLoading,
    isSuperUser,
  } = useUser();

  if (isAuthLoading || isAuthClaimsLoading) {
    return null;
  }

  if (!user || !isSuperUser) {
    return <Navigate to="/" replace />;
  }

  return <Admin />;
};

const PageViewTracker = () => {
  usePageViewTracking();
  return null;
};

function App() {

  useEffect(() => {
    const preloadMaps = () => {
      loadGoogleMaps()
        .catch((error) => console.error(error));
    };

    // Defer non-critical third-party script work until browser idle time.
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(preloadMaps, { timeout: 5000 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(preloadMaps, 2000);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <AppProviders>
      <Router>
        <BaseInviteAcceptProvider>
          <PageViewTracker />
          <div className="App-root">
            <ThemeConsumer>
              <Suspense>
                <Routes>

                <Route path="/auth/reset-password" element={<AuthResetPasswordPage />} />
                <Route path="/po-sign/:token" element={<PoSignPortalPage />} />

                <Route path="/" element={(
                  <Elements stripe={stripePromise}>
                    <Root />
                  </Elements>
                )}>
                  <Route path="admin" element={<SuperUserAdminRoute />} />

                  <Route path="workspace/sidebar" element={<WorkspaceSidebar />} />
                  <Route element={<Main />}>
                    <Route index element={<Home />} />
                    <Route path="invite" element={<BaseInvitePage />} />
                    <Route path="schedule" element={<Schedule />} />
                    <Route path="calendar" element={<Navigate to="/schedule" replace />} />
                    <Route path="channels" element={<Channels />} />
                    <Route path="channels/:id" element={<Channels />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route path="favorites" element={<Favorites />} />
                    <Route path="trash" element={<Trash />} />
                    <Route path="search" element={<Search />} />
                    <Route path="watch/:watchId" element={<WatchPage />} />
                    <Route path="studio" element={<StudioPage />} />
                    <Route path="import/clickup" element={<ClickUpImportPage />} />
                    <Route path="api" element={<Api />} />
                    <Route path="base/:baseKey/profile/:memberId" element={<ProfilePage />} />
                    <Route path="node/:id" element={<NodePage />} />
                    <Route path="collection/:id" element={<CollectionPage />} />
                    {/* Legacy path — prefer /collection/:id */}
                    <Route path="database/:id" element={<DatabasePage />} />
                    <Route path="dashboard/:id" element={<DashboardPage />} />
                  </Route>

                  {/* Fallback route */}
                  <Route path="*" element={<div>Not found</div>} />
                </Route>
              </Routes>
            </Suspense>
          </ThemeConsumer>
        </div>
        </BaseInviteAcceptProvider>
      </Router>
    </AppProviders>
  );
}

// Provides the theme context based on the current app
const ThemeConsumer = ({ children }) => {


  // ----- RENDER ----- //

  return (
    <ThemeProvider>
      <AppThemeVariables />
      <ThemedToastContainer />
      {children}
    </ThemeProvider>
  );
};

/**
 * AppThemeVariables
 *
 * Publishes the current ThemeContext values as reusable global CSS custom properties.
 *
 * @returns {null} No rendered output.
 */
const AppThemeVariables = () => {
  const { theme } = useTheme();

  const themeVariables = useMemo(() => buildThemeCssVariables(theme), [theme]);

  useEffect(() => {
    const rootStyle = document.documentElement?.style;

    if (!rootStyle) {
      return undefined;
    }

    applyThemeCssVariables(rootStyle, themeVariables);

    return () => {
      clearThemeCssVariables(rootStyle, Object.keys(themeVariables));
    };
  }, [themeVariables]);

  return null;
};

const ThemedToastContainer = () => {
  const { theme } = useTheme();


  // ----- RENDER ----- //

  return (
    <div
      style={{
        '--toast-bg-default': theme.background,
        '--toast-bg-info': theme.blue,
        '--toast-bg-success': theme.success,
        '--toast-bg-warning': theme.warning,
        '--toast-bg-error': theme.warning,
      }}
    >
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        transition={Slide}
        hideProgressBar={true}
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        closeOnClick={false}
      />
    </div>
  );
};

export default App;
